import { useTranslation } from 'react-i18next';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { useWebsiteAnalyticsRecent } from '../hooks/useWebsiteAnalyticsRecent';
import { useWebsiteAnalyticsSummary } from '../hooks/useWebsiteAnalyticsSummary';
import { ProductAnalyticsSection } from './ProductAnalyticsSection';
import { WebsiteActivityFeed } from './WebsiteActivityFeed';
import { WebsiteActivityFunnel } from './WebsiteActivityFunnel';
import { WebsiteActivityKpis } from './WebsiteActivityKpis';

function KpiSkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-5 animate-pulse">
          <div className="h-3 w-28 rounded bg-muted" />
          <div className="mt-3 h-8 w-14 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

function WebsiteActivityContent() {
  const { t, i18n } = useTranslation();

  const summaryQuery = useWebsiteAnalyticsSummary(true);
  const recentQuery = useWebsiteAnalyticsRecent(true);

  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const isLoading = summaryQuery.isLoading || recentQuery.isLoading;
  const summaryFailed = summaryQuery.isError || !summaryQuery.data;
  const recentFailed = recentQuery.isError;
  const isFetching = summaryQuery.isFetching || recentQuery.isFetching;

  const refetch = () => {
    void summaryQuery.refetch();
    void recentQuery.refetch();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <KpiSkeletonGrid />
        <LoadingPanel className="py-12" />
      </div>
    );
  }

  if (summaryFailed) {
    return (
      <div className="space-y-3">
        <ErrorPanel message={t('websiteActivity.loadError')} />
        <div className="flex justify-center">
          <Button type="button" variant="outline" size="sm" onClick={refetch}>
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  const recentEvents = recentQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{t('websiteActivity.todayUtc')}</p>
        {isFetching ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icons.RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
            {t('common.refresh')}
          </span>
        ) : null}
      </div>

      <WebsiteActivityKpis summary={summaryQuery.data} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <WebsiteActivityFunnel funnel={summaryQuery.data.funnel} />
        {recentFailed ? (
          <ErrorPanel message={t('websiteActivity.feed.loadError')} />
        ) : recentEvents.length === 0 ? (
          <EmptyPanel
            title={t('websiteActivity.feed.emptyTitle')}
            message={t('websiteActivity.feed.emptyMessage')}
          />
        ) : (
          <WebsiteActivityFeed events={recentEvents} locale={locale} />
        )}
      </div>
    </div>
  );
}

export function WebsiteActivitySection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canRead = user?.permissions.includes('analytics.read') ?? false;

  if (!canRead) {
    return null;
  }

  return (
    <section className="space-y-4" aria-labelledby="website-activity-heading">
      <div>
        <h2 id="website-activity-heading" className="text-lg font-semibold text-foreground">
          {t('websiteActivity.title')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('websiteActivity.subtitle')}</p>
      </div>
      <WebsiteActivityContent />
      <ProductAnalyticsSection />
    </section>
  );
}
